import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HeartPulse, Users, MapPin, CalendarCheck, ClipboardList, BarChart3, FileWarning, ArrowRight, Plus, Activity, RadioTower, CheckCircle2, XCircle } from "lucide-react";
import { PageLoading } from "@/components/shared";
import api from "@/lib/api";

const Metric = ({ label, value, tone = "navy", testid }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={testid}>
    <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`mt-1 font-heading text-3xl font-extrabold text-${tone === "navy" ? "navy" : tone}`}>{value}</div>
  </div>
);

export default function CarePflege() {
  const [d, setD] = useState(null);
  const [kpi, setKpi] = useState(null);
  const [live, setLive] = useState(null);
  const [corr, setCorr] = useState([]);
  const [err, setErr] = useState(false);
  const nav = useNavigate();
  const loadLive = () => api.get("/care/live").then((r) => setLive(r.data)).catch(() => setLive(null));
  const loadCorr = () => api.get("/care/corrections?status=PENDING").then((r) => setCorr(r.data.corrections || [])).catch(() => setCorr([]));
  useEffect(() => {
    api.get("/care/dashboard").then((r) => setD(r.data)).catch(() => setErr(true));
    api.get("/care/reports/kpi-mtd").then((r) => setKpi(r.data)).catch(() => setKpi(null));
    loadLive();
    loadCorr();
    const iv = setInterval(() => { loadLive(); loadCorr(); }, 15000);
    return () => clearInterval(iv);
  }, []);
  const resolveCorr = async (id, decision) => {
    await api.post(`/care/execution-events/${id}/resolve-correction`, { decision }).catch(() => {});
    loadCorr();
  };

  if (err) return (
    <div data-testid="care-locked" className="flex min-h-[50vh] items-center justify-center">
      <div className="card-soft max-w-md p-8 text-center">
        <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-cyan/10 text-cyan-dark"><HeartPulse className="h-7 w-7" /></div>
        <h2 className="mt-5 font-heading text-2xl font-extrabold text-navy">Pflege Modul nicht aktiv</h2>
        <p className="mt-3 text-sm text-slate-500">Aktivieren Sie das Pflege Modul oder Pflege Komplett.</p>
        <button onClick={() => nav("/app/employer/modules")} data-testid="care-goto-modules" className="mt-5 w-full rounded-full bg-cyan px-4 py-2.5 text-sm font-semibold text-white hover:bg-cyan-dark">Pflege Modul buchen</button>
      </div>
    </div>
  );
  if (!d) return <PageLoading />;

  return (
    <div data-testid="care-overview">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2"><HeartPulse className="h-6 w-6 text-cyan-dark" /><h1 className="font-heading text-2xl font-extrabold text-navy">Pflege · Übersicht</h1></div>
      </div>

      {live && (
        <div className="mb-6" data-testid="care-live-strip">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-slate-400"><Activity className="h-3.5 w-3.5 text-cyan-dark" /> Heute · Live-Betrieb</div>
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="live-on-shift">
              <div className="flex items-center gap-2 text-slate-400"><Users className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wider">Mitarbeiter im Dienst</span></div>
              <div className="mt-1 font-heading text-2xl font-extrabold text-navy">{live.workers_on_shift}</div>
            </div>
            <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="live-active-visits">
              <div className="flex items-center gap-2 text-slate-400"><HeartPulse className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wider">Aktive Besuche</span></div>
              <div className="mt-1 font-heading text-2xl font-extrabold text-cyan-dark">{live.active_visits}</div>
            </div>
            <button onClick={() => nav("/app/employer/pflege/reports")} className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition-transform hover:-translate-y-0.5" data-testid="live-mismatches">
              <div className="flex items-center gap-2 text-slate-400"><MapPin className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wider">Standortabweichungen</span></div>
              <div className={`mt-1 font-heading text-2xl font-extrabold ${live.location_mismatches ? "text-amber-600" : "text-emerald-600"}`}>{live.location_mismatches}</div>
            </button>
            <button onClick={() => nav("/app/employer/pflege/reports")} className="rounded-2xl border border-slate-200 bg-white p-4 text-left transition-transform hover:-translate-y-0.5" data-testid="live-open-reviews">
              <div className="flex items-center gap-2 text-slate-400"><FileWarning className="h-4 w-4" /><span className="text-[11px] font-semibold uppercase tracking-wider">Offene Prüfungen</span></div>
              <div className={`mt-1 font-heading text-2xl font-extrabold ${kpi?.open_reviews ? "text-amber-600" : "text-emerald-600"}`}>{kpi?.open_reviews ?? "—"}</div>
            </button>
          </div>
        </div>
      )}

      <div className="mb-6 grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="care-live-feed">
          <div className="mb-3 flex items-center justify-between">
            <div className="flex items-center gap-2"><RadioTower className="h-4 w-4 text-cyan-dark" /><h3 className="font-heading text-base font-extrabold text-navy">Live-Ausführungen</h3></div>
            <button onClick={loadLive} className="text-xs font-semibold text-cyan-dark hover:underline" data-testid="care-feed-refresh">Aktualisieren</button>
          </div>
          <div className="max-h-80 space-y-2 overflow-y-auto">
            {(live?.execution_feed || []).map((e) => (
              <div key={e.id} className="flex items-start justify-between gap-3 rounded-xl bg-slate-50 p-3" data-testid={`feed-${e.id}`}>
                <div className="min-w-0">
                  <div className="truncate text-sm font-semibold text-navy">{e.label || e.event_type}</div>
                  <div className="truncate text-[11px] text-slate-500">{e.worker_name}{e.patient_name ? ` · ${e.patient_name}` : ""}</div>
                </div>
                <span className="shrink-0 rounded-full bg-cyan/10 px-2 py-0.5 text-[10px] font-bold text-cyan-dark">{(e.event_type || "").replace(/_/g, " ")}</span>
              </div>
            ))}
            {(!live?.execution_feed || live.execution_feed.length === 0) && <div className="py-6 text-center text-sm text-slate-400">Noch keine Ausführungen heute.</div>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="care-corrections">
          <div className="mb-3 flex items-center gap-2"><FileWarning className="h-4 w-4 text-amber-600" /><h3 className="font-heading text-base font-extrabold text-navy">Korrekturanträge</h3>{corr.length > 0 && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">{corr.length}</span>}</div>
          <div className="max-h-80 space-y-3 overflow-y-auto">
            {corr.map((c) => (
              <div key={c.id} className="rounded-xl border border-slate-200 p-3" data-testid={`corr-${c.id}`}>
                <div className="text-sm font-semibold text-navy">{c.original?.label || c.original?.event_type || "Ereignis"}</div>
                <div className="mt-0.5 text-[11px] text-slate-500">{c.worker_name}{c.patient_name ? ` · ${c.patient_name}` : ""}{c.patient_id ? ` · ${c.patient_id}` : ""}</div>
                <div className="mt-1 text-xs text-slate-600"><span className="font-semibold">Grund:</span> {c.reason}{c.note ? ` — ${c.note}` : ""}</div>
                {c.intended_value && <div className="text-xs text-slate-600"><span className="font-semibold">Gewünscht:</span> {c.intended_value}</div>}
                <div className="mt-2 flex gap-2">
                  <button onClick={() => resolveCorr(c.id, "APPROVED")} className="inline-flex items-center gap-1 rounded-full bg-emerald-600 px-3 py-1 text-xs font-semibold text-white hover:bg-emerald-700" data-testid={`corr-approve-${c.id}`}><CheckCircle2 className="h-3.5 w-3.5" /> Genehmigen</button>
                  <button onClick={() => resolveCorr(c.id, "REJECTED")} className="inline-flex items-center gap-1 rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-600 hover:bg-slate-50" data-testid={`corr-reject-${c.id}`}><XCircle className="h-3.5 w-3.5" /> Ablehnen</button>
                </div>
              </div>
            ))}
            {corr.length === 0 && <div className="py-6 text-center text-sm text-slate-400">Keine offenen Korrekturanträge.</div>}
          </div>
        </div>
      </div>

      {kpi && (
        <button onClick={() => nav("/app/employer/pflege/reports")} data-testid="care-mtd-strip" className="mb-6 w-full rounded-2xl border border-slate-200 bg-gradient-to-r from-navy to-slate-900 p-5 text-left text-white transition-transform hover:-translate-y-0.5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-cyan"><BarChart3 className="h-4 w-4" /><span className="text-xs font-bold uppercase tracking-wider">Monat aktuell · {kpi.period_label}</span></div>
            <span className="inline-flex items-center gap-1 text-xs font-semibold text-cyan">Zu den Berichten <ArrowRight className="h-3.5 w-3.5" /></span>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-4 sm:grid-cols-4 lg:grid-cols-7">
            {[["Arbeitszeit", `${Math.floor(kpi.work_seconds / 3600)}h`], ["Besuchszeit", `${Math.floor(kpi.visit_seconds / 3600)}h`], ["Fahr-/Wegezeit", `${Math.floor(kpi.travel_seconds / 3600)}h`], ["Kilometer", `${(kpi.distance_m / 1000).toFixed(0)} km`], ["Besuche", kpi.visit_count], ["Ø Besuch", `${Math.floor(kpi.avg_visit_seconds / 60)}m`], ["Offene Prüfungen", kpi.open_reviews]].map(([k, v], i) => (
              <div key={i} data-testid={`care-mtd-${i}`}><div className="text-[10px] uppercase tracking-wider text-slate-400">{k}</div><div className="mt-0.5 font-heading text-lg font-extrabold">{v}</div></div>
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-400">
            <span>Besuchsanteil: <span className="font-semibold text-cyan">{kpi.visit_share}%</span></span>
            <span>Fahr-/Wegeanteil: <span className="font-semibold text-cyan">{kpi.travel_share}%</span></span>
          </div>
        </button>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric label="Patienten gesamt" value={d.total_patients} testid="care-m-total" />
        <Metric label="Aktive Patienten" value={d.active_patients} tone="emerald-600" testid="care-m-active" />
        <Metric label="Neue (30 Tage)" value={d.new_patients_30d} tone="cyan-dark" testid="care-m-new" />
        <Metric label="Offene Stammdaten" value={d.incomplete_master_data} tone="amber-600" testid="care-m-incomplete" />
      </div>

      <div className="mt-6 grid gap-5 lg:grid-cols-2">
        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="care-grade-dist">
          <div className="mb-4 flex items-center gap-2"><BarChart3 className="h-5 w-5 text-cyan-dark" /><h3 className="font-heading text-lg font-extrabold text-navy">Pflegegrad-Verteilung</h3></div>
          <div className="space-y-2">
            {d.grade_distribution.filter((g) => g.count > 0).map((g) => (
              <div key={g.grade} className="flex items-center gap-3 text-sm">
                <span className="w-32 shrink-0 text-slate-600">{g.label}</span>
                <div className="h-2 flex-1 rounded-full bg-slate-100"><div className="h-2 rounded-full bg-cyan" style={{ width: `${Math.min(100, (g.count / Math.max(1, d.total_patients)) * 100)}%` }} /></div>
                <span className="w-8 text-right font-semibold text-navy">{g.count}</span>
              </div>
            ))}
            {d.total_patients === 0 && <div className="text-sm text-slate-400">Noch keine Patienten.</div>}
          </div>
        </div>

        <div className="rounded-2xl border border-slate-200 bg-white p-6" data-testid="care-recent">
          <div className="mb-4 flex items-center justify-between"><h3 className="font-heading text-lg font-extrabold text-navy">Zuletzt aktualisiert</h3>
            <button onClick={() => nav("/app/employer/pflege/patients")} className="inline-flex items-center gap-1 text-sm font-semibold text-cyan-dark hover:underline" data-testid="care-all-patients">Alle Patienten <ArrowRight className="h-4 w-4" /></button></div>
          <div className="divide-y divide-slate-100">
            {d.recent.map((p) => (
              <button key={p.id} onClick={() => nav(`/app/employer/pflege/patients/${p.id}`)} className="flex w-full items-center justify-between py-2.5 text-left hover:bg-slate-50" data-testid={`care-recent-${p.id}`}>
                <div><div className="text-sm font-semibold text-navy">{p.name}</div><div className="font-mono text-xs text-slate-400">{p.patient_number}</div></div>
                <ArrowRight className="h-4 w-4 text-slate-300" />
              </button>
            ))}
            {d.recent.length === 0 && <div className="py-2 text-sm text-slate-400">—</div>}
          </div>
        </div>
      </div>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4" data-testid="care-upcoming">
        {[["Disposition", MapPin, "/app/employer/pflege/dispatch"], ["Touren", MapPin, "/app/employer/pflege/tours"], ["Besuche", CalendarCheck, "/app/employer/pflege/visits"], ["Patienten", Users, "/app/employer/pflege/patients"]].map(([label, Icon, to]) => (
          <button key={label} onClick={() => nav(to)} data-testid={`care-tile-${to.split("/").pop()}`} className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white p-4 text-left text-navy transition-transform hover:-translate-y-0.5 hover:shadow-md">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-cyan/10 text-cyan-dark"><Icon className="h-5 w-5" /></div>
            <div><div className="text-sm font-bold">{label}</div><div className="text-[11px] text-slate-400">Öffnen</div></div>
          </button>
        ))}
      </div>
    </div>
  );
}
